import { DOCUMENT_CONTENT_TYPES, MAX_DOCUMENT_BYTES, type PermissionRequest } from '@gestion-permisos/contracts';
import { type FormEvent, useId, useState } from 'react';
import { buttonClasses } from '../components/button-styles.js';
import { ProblemMessage } from '../components/problem-message.js';
import { usePermissionTypes } from './catalog-api.js';
import { useAttachDocument, useDownloadDocument, useRemoveDocument } from './documents-api.js';

const BUTTON = buttonClasses('outline');
const PRIMARY = buttonClasses();
const TYPE_LABELS: Record<string, string> = { 'application/pdf': 'PDF', 'image/jpeg': 'JPEG', 'image/png': 'PNG' };
const SIZE = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 });

/** Tamaño legible: `850 KB`, `2,4 MB`. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${SIZE.format(Math.max(1, Math.round(bytes / 1024)))} KB`;
  return `${SIZE.format(bytes / (1024 * 1024))} MB`;
}

/** Tipos que ofrece el selector de archivos (el del detalle y el del alta). */
export const DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

/**
 * Comprobación de **forma** antes de enviar (tipo y tamaño), para avisar sin esperar a la API. La API decide
 * siempre: comprueba también la firma del contenido.
 */
export function documentProblem(file: File | undefined): string | undefined {
  if (!file) return 'Elige un archivo.';
  if (!(DOCUMENT_CONTENT_TYPES as readonly string[]).includes(file.type)) return 'Solo se admiten archivos PDF, JPEG o PNG.';
  if (file.size === 0) return 'El archivo está vacío.';
  if (file.size > MAX_DOCUMENT_BYTES) return 'El archivo supera los 10 MB.';
  return undefined;
}

/**
 * Documento de la solicitud (09 §6.11, D-10). Cualquiera que ve la solicitud puede **descargarlo** (por la
 * API). Solo quien la pidió, en borrador (`editable`), puede **adjuntar** uno (PDF, JPEG o PNG de hasta
 * 10 MB) o **retirarlo**. Si el tipo exige documento, se avisa antes de enviar; si no lo permite, no se ofrece.
 */
const SECTION_BOX = 'mt-6 rounded-md border border-border bg-card p-4';

/** `className` sustituye la caja por defecto (borde, fondo y margen), p. ej. por la de una tarjeta. */
export function RequestDocument({ request, editable, className = SECTION_BOX }: { request: PermissionRequest; editable: boolean; className?: string }) {
  const attach = useAttachDocument(request.id);
  const remove = useRemoveDocument(request.id);
  const download = useDownloadDocument(request.id);
  const titleId = useId();
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const document = request.document;

  // Sin documento y sin poder adjuntarlo: la sección no aporta nada (ni pide el catálogo).
  if (!document && !editable) return null;

  const busy = attach.isPending || remove.isPending;
  const error = attach.error ?? remove.error ?? download.error;

  return (
    <section aria-labelledby={titleId} className={`space-y-3 ${className}`}>
      <h2 id={titleId} className="text-lg font-medium">
        Documento justificativo
      </h2>
      {notice ? <output className="block rounded-md border border-border bg-muted p-3 text-sm">{notice}</output> : null}
      {error ? <ProblemMessage error={error} /> : null}

      {document ? (
        <div className="flex flex-wrap items-center gap-3">
          <p>
            <span className="font-medium">{document.fileName}</span>{' '}
            <span className="text-sm text-muted-foreground">
              ({TYPE_LABELS[document.contentType] ?? document.contentType}, {formatBytes(document.sizeBytes)})
            </span>
          </p>
          <button
            type="button"
            className={BUTTON}
            disabled={download.isPending}
            onClick={() => {
              download.reset();
              download.mutate({ id: document.id, fileName: document.fileName });
            }}
          >
            Descargar
          </button>
          {editable ? (
            <button
              type="button"
              className={BUTTON}
              disabled={busy}
              onClick={() => {
                attach.reset();
                setNotice(undefined);
                remove.mutate(document.id, { onSuccess: () => setNotice('Documento retirado.') });
              }}
            >
              Retirar documento
            </button>
          ) : null}
        </div>
      ) : null}

      {editable && !document ? (
        <UploadForm
          request={request}
          pending={busy}
          onUpload={(file) => {
            setNotice(undefined);
            remove.reset();
            attach.mutate(file, { onSuccess: () => setNotice('Documento adjuntado.') });
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Adjuntar (solo en el borrador propio). Lee el catálogo de tipos para avisar si el tipo exige documento o no
 * lo admite; si el tipo no aparece (p. ej., inactivo), ofrece el formulario y la API decide.
 */
function UploadForm({ request, pending, onUpload }: { request: PermissionRequest; pending: boolean; onUpload: (file: File) => void }) {
  const types = usePermissionTypes();
  const type = types.data?.items.find((item) => item.id === request.permissionTypeId);
  const inputId = useId();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileError, setFileError] = useState<string | undefined>(undefined);

  if (type?.allowsDocument === false) return <p className="text-muted-foreground">Este tipo de permiso no admite documentos.</p>;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const problem = documentProblem(file);
    setFileError(problem);
    if (problem || !file) return;
    onUpload(file);
  };

  return (
    <form noValidate onSubmit={submit} className="space-y-2">
      {type?.requiresDocument ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Este tipo de permiso exige un documento: adjúntalo antes de enviar la solicitud.
        </p>
      ) : null}
      <label htmlFor={inputId} className="block text-sm font-medium">
        Archivo (PDF, JPEG o PNG, hasta 10 MB)
      </label>
      <input
        id={inputId}
        type="file"
        accept={DOCUMENT_ACCEPT}
        onChange={(event) => {
          setFile(event.target.files?.[0]);
          setFileError(undefined);
        }}
        aria-invalid={fileError !== undefined}
        aria-describedby={fileError !== undefined ? `${inputId}-error` : undefined}
        className="block text-sm"
      />
      {fileError ? (
        <p id={`${inputId}-error`} className="text-sm text-red-700">
          {fileError}
        </p>
      ) : null}
      <button type="submit" className={PRIMARY} disabled={pending}>
        Adjuntar documento
      </button>
    </form>
  );
}
